import { DayDepthChartWidget } from '@go-widgets/well-logs-widget';
import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon } from '@repo/go-icons';
import { Button, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useService } from 'src/packages/di';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { DayDepthChartWidgetEntity } from './DayDepthChartWidget.entity';
import styles from './DayDepthChartWidget.module.scss';
import { DayDepthChartWidgetStore } from './DayDepthChartWidget.store';

type WrapperProps = {
  entity: DayDepthChartWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: DayDepthChartWidgetStore;
  entity: DayDepthChartWidgetEntity;
  options: TWidgetRendererOptions;
};

export const WellDayDepthWidgetWrapper = observer(function WellDayDepthWidgetWrapper({
  entity,
  options,
}: WrapperProps) {
  const [store, setStore] = useState<DayDepthChartWidgetStore | null>(null);

  const storeService = useService('widgetStoreService');

  const navigate = useNavigate();

  useEffect(() => {
    if (store) return;

    function redirectToBasicParameters(wellId: number | null, wellboreId?: number | null) {
      const queryParams = new URLSearchParams();

      if (wellId) {
        queryParams.set('wellId', wellId.toString());
      }

      if (wellboreId) {
        queryParams.set('wellboreId', wellboreId.toString());
      }

      const path = `/settings?section=basicParameters&${queryParams.toString()}`;

      navigate(path);
    }

    const _store = storeService.getStore<DayDepthChartWidgetEntity, DayDepthChartWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new DayDepthChartWidgetStore(
        entity,
        options.groupSelectStore,
        redirectToBasicParameters,
        options.wellListEntity,
      );

      storeService.setStore<DayDepthChartWidgetEntity, DayDepthChartWidgetStore>(entity, newStore);

      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, navigate, options.wellListEntity]);

  if (!store) return null;

  return <DayDepthWidget store={store} entity={entity} options={options} />;
});

export const DayDepthWidget = observer(function DayDepthWidget({ entity, options, store }: Props) {
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);

  const { setGroupForWellLogs, exitFullScreen, isReady, effect } = store;

  const { t } = useTranslation();

  const { wellId, isFullscreen, state, setFullscreen, wellboreName } = entity;

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.dayDepthWidgetWrapper} ref={(ref) => setWrapperElement(ref)}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellLogs}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => exitFullScreen()}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />
      {wrapperElement && (
        <div className={styles.wrapper}>
          {state ? <DayDepthChartWidget widgetState={state} externalContainer={wrapperElement} /> : <NoWellChosen />}
        </div>
      )}
    </div>
  );
});
